import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import pluralize from 'pluralize';
import { LineItem } from 'common/display/LineItem';
import { dollarAmount } from 'common/display/Money/DollarAmount';
import formatShortDate from 'common/utils/Dates/formatShortDate';
import getTimeDifference from 'common/utils/Dates/getTimeDifference';

const AmountRequestedLineItem = ({
  authorization, feeScheduleProgram, previousAuth, showOriginalValues,
}) => {
  const unitAmount = authorization.requested_unit_amount;
  const value = unitAmount ?
    `${unitAmount} ${pluralize(feeScheduleProgram.unit, unitAmount)}` :
    dollarAmount(authorization.requested_cents, true);

  const originalValue = showOriginalValues ? (() => {
    const originalUnitAmount = previousAuth.requested_unit_amount;
    return originalUnitAmount ?
      `${originalUnitAmount} ${pluralize(feeScheduleProgram.unit, originalUnitAmount)}` :
      dollarAmount(previousAuth.requested_cents, true);
  })() : null;

  return (
    <LineItem field="Amount Requested" dataTestElement="amount-requested">
      {value}
      {originalValue && (
        <span className="line-through ml-2">{originalValue}</span>
      )}
    </LineItem>
  );
};

const ServiceDeliveryDateLineItem = ({
  isSingleDateRequested,
  dateRequestedStartsAt,
  dateRequestedEndsAt,
  previousAuth,
  showOriginalValues,
}) => {
  const fieldName = isSingleDateRequested ? 'Service Delivery Date Requested' : 'Service Delivery Dates Requested';
  const testElement = isSingleDateRequested ? 'service-delivery-date' : 'service-delivery-dates';
  const value = isSingleDateRequested ? dateRequestedEndsAt : `${dateRequestedStartsAt} - ${dateRequestedEndsAt}`;

  const originalValue = showOriginalValues ? (() => {
    const {
      requested_starts_at: originalStartsAt,
      requested_ends_at: originalEndsAt,
    } = previousAuth;
    const formattedOriginalStartsAt = formatShortDate(originalStartsAt);
    const formattedOriginalEndsAt = formatShortDate(originalEndsAt);
    const isOriginalSingleDate = formattedOriginalStartsAt === formattedOriginalEndsAt;
    return isOriginalSingleDate ? formattedOriginalEndsAt : `${formattedOriginalStartsAt} - ${formattedOriginalEndsAt}`;
  })() : null;

  return (
    <LineItem field={fieldName} dataTestElement={testElement}>
      {value}
      {originalValue && (
        <span className="line-through ml-2">{originalValue}</span>
      )}
    </LineItem>
  );
};

const IcdCodeLineItem = ({
  isFspLoaded,
  feeScheduleProgram,
  authorization,
  previousAuth,
  showOriginalValues,
}) => {
  const shouldShow = isFspLoaded && feeScheduleProgram?.cm_code_required_on_authorization;

  if (!shouldShow) return null;

  const codes = authorization.clinical_modifications?.map((cm) => cm.code).join(',');
  const originalCodes = showOriginalValues &&
    previousAuth.clinical_modifications ?
    previousAuth.clinical_modifications.map((cm) => cm.code).join(',') :
    null;

  return (
    <LineItem field="ICD-10 Code" dataTestElement="icd-10-code">
      {codes}
      {originalCodes && (
        <span className="line-through ml-2">{originalCodes}</span>
      )}
    </LineItem>
  );
};

const SubmittedByLineItem = ({ authorization }) => {
  const isLoaded = !isEmpty(authorization.requester) && !isEmpty(authorization.requester.provider);

  return (
    <LineItem
      field="Submitted By"
      dataTestElement="submitted-by"
      isLoaded={isLoaded}
    >
      {authorization.requester?.full_name}
      <br />
      ({authorization.requester?.provider?.name})
    </LineItem>
  );
};

const RequestDateLineItem = ({ authorization, dateRequested }) => {
  const isLoaded = !!dateRequested || authorization?.auto_approved;
  const value = authorization?.auto_approved ?
    formatShortDate(authorization.updated_at) :
    formatShortDate(dateRequested);

  return (
    <LineItem
      field="Date of Request"
      isLoaded={isLoaded}
      dataTestElement="request-date"
    >
      {value}
    </LineItem>
  );
};

const TimeInQueueLineItem = ({ authorization }) => {
  const shouldShow = authorization.state === 'requested';

  if (!shouldShow) return null;

  return (
    <LineItem field="Time in Queue" dataTestElement="time-in-queue">
      {getTimeDifference(authorization.updated_at)}
    </LineItem>
  );
};

const AuthorizationSummarySection = ({
  authorization,
  feeScheduleProgram,
  dateRequested,
  dateRequestedStartsAt,
  dateRequestedEndsAt,
  isSingleDateRequested,
  isFspLoaded,
  previousAuth,
  showOriginalValues,
}) => (
  <dl className="m-0 grid grid-cols-2 gap-x-8 gap-y-2">
    <AmountRequestedLineItem
      authorization={authorization}
      feeScheduleProgram={feeScheduleProgram}
      previousAuth={previousAuth}
      showOriginalValues={showOriginalValues}
    />
    <ServiceDeliveryDateLineItem
      isSingleDateRequested={isSingleDateRequested}
      dateRequestedStartsAt={dateRequestedStartsAt}
      dateRequestedEndsAt={dateRequestedEndsAt}
      previousAuth={previousAuth}
      showOriginalValues={showOriginalValues}
    />
    <IcdCodeLineItem
      isFspLoaded={isFspLoaded}
      feeScheduleProgram={feeScheduleProgram}
      authorization={authorization}
      previousAuth={previousAuth}
      showOriginalValues={showOriginalValues}
    />
    <SubmittedByLineItem authorization={authorization} />
    <RequestDateLineItem
      authorization={authorization}
      dateRequested={dateRequested}
    />
    <TimeInQueueLineItem authorization={authorization} />
  </dl>
);

AmountRequestedLineItem.propTypes = {
  authorization: PropTypes.shape({
    requested_unit_amount: PropTypes.number,
    requested_cents: PropTypes.number,
  }).isRequired,
  feeScheduleProgram: PropTypes.shape({
    unit: PropTypes.string,
  }).isRequired,
  previousAuth: PropTypes.shape({
    requested_unit_amount: PropTypes.number,
    requested_cents: PropTypes.number,
  }),
  showOriginalValues: PropTypes.bool,
};

AmountRequestedLineItem.defaultProps = {
  previousAuth: {},
  showOriginalValues: false,
};

ServiceDeliveryDateLineItem.propTypes = {
  isSingleDateRequested: PropTypes.bool.isRequired,
  dateRequestedStartsAt: PropTypes.string.isRequired,
  dateRequestedEndsAt: PropTypes.string.isRequired,
  previousAuth: PropTypes.shape({
    requested_starts_at: PropTypes.string,
    requested_ends_at: PropTypes.string,
  }),
  showOriginalValues: PropTypes.bool,
};

ServiceDeliveryDateLineItem.defaultProps = {
  previousAuth: {},
  showOriginalValues: false,
};

IcdCodeLineItem.propTypes = {
  isFspLoaded: PropTypes.bool.isRequired,
  feeScheduleProgram: PropTypes.shape({
    cm_code_required_on_authorization: PropTypes.bool,
  }).isRequired,
  authorization: PropTypes.shape({
    clinical_modifications: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
    })),
  }).isRequired,
  previousAuth: PropTypes.shape({
    clinical_modifications: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
    })),
  }),
  showOriginalValues: PropTypes.bool,
};

IcdCodeLineItem.defaultProps = {
  previousAuth: {},
  showOriginalValues: false,
};

SubmittedByLineItem.propTypes = {
  authorization: PropTypes.shape({
    requester: PropTypes.shape({
      full_name: PropTypes.string,
      provider: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
  }).isRequired,
};

RequestDateLineItem.propTypes = {
  authorization: PropTypes.shape({
    auto_approved: PropTypes.bool,
    updated_at: PropTypes.string,
  }).isRequired,
  dateRequested: PropTypes.string,
};

RequestDateLineItem.defaultProps = {
  dateRequested: null,
};

TimeInQueueLineItem.propTypes = {
  authorization: PropTypes.shape({
    state: PropTypes.string.isRequired,
    updated_at: PropTypes.string.isRequired,
  }).isRequired,
};

AuthorizationSummarySection.propTypes = {
  authorization: PropTypes.shape({
    requested_unit_amount: PropTypes.number,
    requested_cents: PropTypes.number,
    clinical_modifications: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
    })),
    requester: PropTypes.shape({
      full_name: PropTypes.string,
      provider: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
    auto_approved: PropTypes.bool,
    updated_at: PropTypes.string,
    state: PropTypes.string.isRequired,
  }).isRequired,
  feeScheduleProgram: PropTypes.shape({
    unit: PropTypes.string,
    cm_code_required_on_authorization: PropTypes.bool,
  }).isRequired,
  dateRequested: PropTypes.string,
  dateRequestedStartsAt: PropTypes.string.isRequired,
  dateRequestedEndsAt: PropTypes.string.isRequired,
  isSingleDateRequested: PropTypes.bool.isRequired,
  isFspLoaded: PropTypes.bool.isRequired,
  previousAuth: PropTypes.shape({
    requested_unit_amount: PropTypes.number,
    requested_cents: PropTypes.number,
    requested_starts_at: PropTypes.string,
    requested_ends_at: PropTypes.string,
    clinical_modifications: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
    })),
  }),
  showOriginalValues: PropTypes.bool,
};

AuthorizationSummarySection.defaultProps = {
  dateRequested: null,
  previousAuth: {},
  showOriginalValues: false,
};

export default AuthorizationSummarySection;
